/*
 * Grafica de linea del tablero: ejes con ticks en COP, gridlines, area bajo
 * la curva, puntos con hover y tooltip. Se dibuja en SVG y solo depende de
 * carthelp, para que sirva igual en las ramas con y sin motor de graficas.
 */
import { useRef, useState } from "react";

import { buildTicks, fmtCOP, htmlTheme } from "./utils/carthelp";

const AXIS_W = 96;
const XLBL_H = 22;

/**
 * Renderiza el tooltip flotante del punto activo
 * @param {Object} props Propiedades del componente
 * @param {Object} props.tip Estado del tooltip { x, y, title, value } o null
 */
const Pointtip = ({ tip }) => {
  if (!tip) return null;

  return (
    <div
      style={{
        position: "absolute",
        left: tip.x + 12,
        top: tip.y + 12,
        background: "rgba(15,22,50,.85)",
        color: "#fff",
        borderRadius: 6,
        padding: "8px 10px",
        fontSize: 11.5,
        pointerEvents: "none",
        zIndex: 50,
        whiteSpace: "nowrap",
        boxShadow: "0 4px 12px rgba(0,0,0,.25)",
      }}
    >
      <div style={{ fontWeight: 700, marginBottom: 2 }}>{tip.title}</div>
      <div>{tip.value}</div>
    </div>
  );
};

/**
 * Renderiza una grafica de linea con ejes, gridlines y puntos
 * @param {Object} props Propiedades del componente
 * @param {Array} props.data Puntos [{ label, value }]
 * @param {number} props.height Alto total del area de la grafica
 */
const Linechrt = ({ data = [], height = 300 }) => {
  const { ticks, top } = buildTicks(Math.max(1, ...data.map((mItem) => Math.abs(mItem.value))));
  const containerRef = useRef(null);
  const [mTip, setTip] = useState(null);

  //Coordenadas en porcentaje: el SVG se estira con el contenedor
  const nLast = Math.max(1, data.length - 1);
  const posX = (nIndex) => (data.length === 1 ? 50 : (nIndex / nLast) * 100);
  const posY = (nValue) => 100 - (Math.abs(nValue) / top) * 100;
  const sLine = data.map((mItem, nIndex) => `${posX(nIndex)},${posY(mItem.value)}`).join(" ");

  //Sigue el mouse para ubicar el tooltip del punto
  const handleMove = (oEvent, mItem) => {
    const oRect = containerRef.current?.getBoundingClientRect();
    if (!oRect) return;
    setTip({
      x: oEvent.clientX - oRect.left,
      y: oEvent.clientY - oRect.top,
      title: mItem.label,
      value: fmtCOP(mItem.value),
    });
  };

  return (
    <div
      ref={containerRef}
      style={{ display: "flex", height, position: "relative" }}
      onMouseLeave={() => setTip(null)}
    >
      <Pointtip tip={mTip} />
      <div style={{ width: AXIS_W, flexShrink: 0, position: "relative" }}>
        {ticks.map((nTick) => (
          <span
            key={nTick}
            style={{
              position: "absolute",
              right: 8,
              bottom: `calc(${XLBL_H}px + (100% - ${XLBL_H}px) * ${nTick / top} - 6px)`,
              fontSize: 10,
              color: htmlTheme.muted,
              whiteSpace: "nowrap",
            }}
          >
            {fmtCOP(nTick)}
          </span>
        ))}
      </div>
      <div style={{ flex: 1, position: "relative" }}>
        {ticks.map((nTick) => (
          <div
            key={nTick}
            style={{
              position: "absolute",
              left: 0,
              right: 0,
              bottom: `calc(${XLBL_H}px + (100% - ${XLBL_H}px) * ${nTick / top})`,
              borderBottom: `1px solid ${nTick === 0 ? "#D5DAE8" : "#EDEFF7"}`,
            }}
          />
        ))}
        <div style={{ position: "absolute", left: 0, right: 0, top: 0, bottom: XLBL_H }}>
          {/* preserveAspectRatio none estira el trazo, non-scaling-stroke le devuelve el grosor */}
          <svg
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
            style={{ width: "100%", height: "100%", display: "block", overflow: "visible" }}
          >
            <polygon points={`0,100 ${sLine} 100,100`} fill="rgba(18,36,89,0.08)" />
            <polyline
              points={sLine}
              fill="none"
              stroke={htmlTheme.navy}
              strokeWidth={2}
              strokeLinejoin="round"
              vectorEffect="non-scaling-stroke"
            />
          </svg>
          {data.map((mItem, nIndex) => (
            <div
              key={nIndex}
              onMouseMove={(oEvent) => handleMove(oEvent, mItem)}
              style={{
                position: "absolute",
                left: `${posX(nIndex)}%`,
                bottom: `${(Math.abs(mItem.value) / top) * 100}%`,
                width: 10,
                height: 10,
                marginLeft: -5,
                marginBottom: -5,
                borderRadius: "50%",
                background: htmlTheme.coral,
                border: "2px solid #fff",
                boxSizing: "border-box",
              }}
            />
          ))}
        </div>
        <div style={{ position: "absolute", left: 0, right: 0, bottom: 0, height: XLBL_H }}>
          {data.map((mItem, nIndex) => (
            <span
              key={nIndex}
              style={{
                position: "absolute",
                left: `${posX(nIndex)}%`,
                transform: "translateX(-50%)",
                fontSize: 11,
                color: htmlTheme.muted,
                whiteSpace: "nowrap",
                paddingTop: 6,
              }}
            >
              {mItem.label}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Linechrt;
